/**
 * Public CustomPage — 自訂頁面 public renderer.
 *
 * Fetches /api/cms/public/custom-pages/:slug at runtime, seeded from the
 * build-time snapshot so prerendered HTML is complete. Drafts are invisible
 * unless the request carries a valid ?preview_token= (then is_draft=true).
 *
 * ⚠️ Landmines (see plan §5.2):
 *   1. Never pass `path` to <SEO> — it strips the locale segment and every
 *      locale canonicalizes to the same ghost URL.
 *   2. hreflang is auto-generated by <SEO> from the router pathname.
 *   3. verify-canonicals.mjs is a hard gate — canonical must equal the served
 *      URL with locale prefix + trailing slash.
 */
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import SEO from '@/utils/seo'
import { buildArticle, buildBreadcrumbList } from '@/utils/structuredData'
import RichBody from '@/components/content/RichBody'
import NotFoundPage from '@/pages/NotFoundPage'
import { useCmsCustomPage } from '@/hooks/useCmsCustomPage'

export default function CustomPage() {
  const { t } = useTranslation()
  const { slug = '' } = useParams<{ slug: string }>()
  const { data, loading, error } = useCmsCustomPage(slug)

  if (loading && !data) {
    return (
      <section className="py-section-y px-section-x">
        <div className="mx-auto max-w-5xl px-6 py-6 lg:px-10 lg:py-8">
          <p className="text-ink-muted">{t('common.loading')}</p>
        </div>
      </section>
    )
  }
  // An unknown slug is a 404, not a redirect: `Navigate to="/"` would drop the
  // locale prefix and send the visitor to the homepage with a 200 status.
  if (error === 'not_found' || !data) {
    return <NotFoundPage />
  }

  const seoMeta = data.seo as {
    metaTitle?: string
    metaDescription?: string
    ogImageMediaId?: number | null
    canonicalUrl?: string
    robotsDirective?: string
  }

  // The page's own route, e.g. `/about-harbour`. It is stored per page so the
  // canonical/JSON-LD URLs stay stable even if the slug is renamed later.
  const route = data.route || `/${data.slug}`
  const headline = seoMeta.metaTitle ?? data.title
  const publishedIso = data.published_at
    ? new Date(data.published_at).toISOString()
    : new Date(data.updated_at || 0).toISOString()

  return (
    <>
      <SEO
        title={headline}
        description={seoMeta.metaDescription ?? data.excerpt}
        type="article"
        ogImage={undefined}
        noindex={/^noindex$/i.test(seoMeta.robotsDirective ?? '')}
        articlePublishedTime={publishedIso}
        articleAuthor={t('common.brand')}
        jsonLd={[
          buildArticle({
            path: route,
            headline,
            alternativeHeadline: data.excerpt,
            datePublished: publishedIso,
            dateModified: new Date(data.updated_at || 0).toISOString(),
            author: t('common.brand'),
            image: '/og-image-1200x630.png',
            type: 'Article',
          }),
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.products'), path: '/products' },
            { name: data.title, path: route },
          ]),
        ]}
      />
      <article className="prose-yada">
        <header className="mb-8">
          <h1 className="mb-3">{data.title}</h1>
          {data.excerpt && (
            <p className="text-lg text-ink-soft italic border-l-4 border-primary pl-4 mb-8">
              {data.excerpt}
            </p>
          )}
        </header>
        <div className="prose prose-base max-w-none text-ink">
          <RichBody markdown={data.body_md} imageClassName="rounded-yada-md border border-border shadow-yada my-4" />
        </div>
      </article>
    </>
  )
}
